import { toast } from 'react-hot-toast';
import { ChangeEvent, useEffect, useState } from 'react';
import { Button } from '../../../../../components/ui/Button/Button';
import S from './AudioSchemaSettingsDeviceItemStyles';
import Modal from '../../../../../components/common/Modal/Modal';
import InfoModal from '../../../../../components/common/InfoModal/InfoModal';
import useGetAudioSchemas from '../../../../../API/hooks/devices/useGetAudioSchemas';
import useGetFleetId from '../../../../../API/hooks/useGetFleetId';
import Select from '../../../../../components/common/Select/Select';
import usePostApplyAudioSchemaToFleet from '../../../../../API/hooks/devices/usePostApplyAudioSchemaToFleet';
import usePatchActivateFleetAudioSchema from '../../../../../API/hooks/devices/usePatchActivateFleetAudioSchema';
import useGetAudioSchemaDevice from '../../../../../API/hooks/devices/useGetAudioSchemaDevice';
import useGetAudioSchemaFleet from '../../../../../API/hooks/devices/useGetAudioSchemaFleet';
import useGetRole from '../../../../../API/hooks/useGetRole';
import usePostApplyAudioSchemaToDevice from '../../../../../API/hooks/devices/usePostApplyAudioSchemaToDevice';

type Props = {
  deviceId: string,
  isOnline?: boolean;
  toggleModal: (closed: boolean) => void;
};

function AudioSchemaSettingsDeviceItem({
  deviceId, isOnline, toggleModal,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const {
    isFleetViewer,
    isFleetManager,
  } = useGetRole();
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  const onSubmitSuccess = (): void => {
    if (!isOnline) {
      toggleDeviceOfflineSaveModal(true);
    }
    toast.success('Schema was applied successfully');
  };
  const onSubmitFailed = (e: any): void => {
    toast.error(`Schema applying error: ${e}`);
  };
  const { data: availableSchemas, isLoading: availableSchemasLoading } = useGetAudioSchemas(fleetId, '?limit=1000&skip=0');
  const { data: audioSchemaFleet, isLoading: audioSchemaFleetLoading } = useGetAudioSchemaFleet(fleetId);
  const { data: audioSchemaDevice, isLoading: audioSchemaDeviceLoading } = useGetAudioSchemaDevice(fleetId, deviceId);
  const { mutate: applySchema } = usePostApplyAudioSchemaToDevice(onSubmitSuccess, onSubmitFailed);
  const fleetSchemaIsActive = audioSchemaFleet?.isActive as boolean;
  const [selectedSchema, setSelectedSchema] = useState(audioSchemaDevice?.audioBundleId || '');

  const schemaIsChanged = audioSchemaFleet?.audioBundleId !== selectedSchema;

  useEffect(() => setSelectedSchema(audioSchemaDevice?.audioBundleId || ''), [audioSchemaDevice]);

  const defaultListItem = [{
    label: `${availableSchemasLoading || audioSchemaFleetLoading ? 'Loading...' : 'Select schema'}`,
    value: '',
  }];

  const schemasList = availableSchemas?.results.map((schema) => ({
    label: schema.name,
    value: schema._id,
  })) || [];

  const schemaOnChange = (e: any): void => {
    setSelectedSchema(e.target.value);
  };

  const saveAudioSettings = (): void => {
    if (schemaIsChanged && selectedSchema !== '' && !fleetSchemaIsActive) {
      applySchema({
        fleetId,
        deviceId,
        audioBundleId: selectedSchema,
      });
    }
  };

  return (
    <S.Wrapper>
      <p>Audio Schema Configuration:</p>
      <S.RowContainer className={audioSchemaDeviceLoading || audioSchemaFleetLoading || availableSchemasLoading ? '' : 'hidden'}>
        <p className="message loading">Loading...</p>
      </S.RowContainer>
      <div className={`change-schema${fleetSchemaIsActive || audioSchemaDeviceLoading ? ' hidden' : ''}`}>
        <p>Current schema:</p>
        <Select
          value={selectedSchema as string}
          options={[...defaultListItem, ...schemasList]}
          onChange={schemaOnChange}
          disabled={isFleetManager || isFleetViewer || fleetSchemaIsActive}
          simple
        />
      </div>
      <S.RowContainer className={fleetSchemaIsActive ? '' : 'hidden'}>
        <p className="message warning">Global fleet setting was applied. No possibility to configure audio schema for individual device.</p>
      </S.RowContainer>
      <div className="actions-container">
        <Button
          height="50px"
          width="135px"
          onClick={() => toggleModal(false)}
          type="button"
          disabled={isFleetManager || isFleetViewer}
        >
          Close
        </Button>
        <Button
          className="save-button"
          height="50px"
          width="135px"
          type="button"
          onClick={saveAudioSettings}
          disabled={availableSchemasLoading || audioSchemaFleetLoading || selectedSchema === '' || isFleetManager || isFleetViewer || fleetSchemaIsActive || !schemaIsChanged}
        >
          Save
        </Button>
      </div>
      {confirmSaveModalOpen && (
        <Modal
          title="Audio schema settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New audio schema was submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Audio schema settings"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default AudioSchemaSettingsDeviceItem;
